<!-- @prettier -->
<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div
        v-if="!showSpecificRecordDetails"
        class="header-extra"
      >
        <ColumnToggle
          :table-name="'publisherMaps'"
          :user-settings="userSettings"
          :settings-status="getUserSettingsStatus"
          :column-settings="columnSettings"
          @onChange="updateUserVisibleColumns"
        />
        <button
          class="btn btn-sm widget-header-button m-r-5"
          @click="exportToCSV()"
        >
          {{ $t('EXPORT_CSV') }}
        </button>
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('PUBLISHERS_SEARCH_PH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!isFetchingPublishers"
      class="m-b-15 m-t-10"
    >
      <!-- Create Button -->
      <button
        v-if="hasCreateAccess && !showSpecificRecordDetails"
        type="button"
        :disabled="formActive && !activePublisher"
        class="btn btn-success btn-sm"
        @click="showForm(null, $event)"
      >
        <i class="fa fa-plus" />
        <span v-t="'PUBLISHERS_ADD_BUTTON'" />
      </button>

      <!-- Go to Publishers -->
      <button
        v-if="showSpecificRecordDetails"
        class="btn btn-default btn-sm"
        @click="goToPublishers"
      >
        <i class="fa fa-arrow-left" />
        <span v-t="'PUBLISHER_SHOW_ALL'" />
      </button>
    </div>

    <!-- Table Container -->
    <div
      v-if="!isFetchingPublishers"
      class="grid_table_holder"
    >
      <v-table
        :data="publishers"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              v-for="col in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :default-sort="columnSettings[col].defaultSort"
              :sort-key="columnSettings[col].name"
            />
            <th
              v-if="!isMobile"
              v-t="'ACTIONS_COLUMN_HEADER'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <!-- Empty Publishers row - START -->
          <tr v-if="!showAddNewForm && publishers.length == 0">
            <td
              v-t="'PUBLISHERS_HEADER_NONE'"
              colspan="100"
            />
          </tr>
          <!-- Empty Publishers row - END -->

          <!-- Create New Form -->
          <tr
            v-if="showAddNewForm && hasCreateAccess"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <PublisherForm
                v-if="!showNewPublisherInvites"
                is-inline-table="true"
                :show-loader="isCreatePublisherLoading"
                :account-settings="accountSettings"
                @cancel="hideForm"
                @submit="handleAddPublisherSubmit"
              />
              <!-- Publisher Invites -->
              <PublisherInvites
                v-if="showNewPublisherInvites && invitesForPublisher"
                :publisher-id="invitesForPublisher.id"
                :close-btn-text="
                  isNewUserToNewPublisherInvited ? 'Close' : 'Skip'
                "
                @close="onNewPublisherInvitesClose"
                @created-new-invite="onAddingNewInviteToNewPublisher"
              >
                <template v-slot:title>
                  <p>
                    Invite Users to
                    <strong>{{ invitesForPublisher.name }}</strong>
                  </p>
                </template>
              </PublisherInvites>
            </td>
          </tr>
          <!-- Create new publisher form row - END -->

          <!-- Existing publishers list - START -->
          <template v-for="(publisher, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == publisher.id)"
              :key="publisher.id"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showAddNewForm"
                  @click="displayMobileAction(publisher.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>

              <!-- name -->
              <td
                v-if="userVisibleColumns.includes('name')"
                :align="columnSettings['name'].rowAlign"
              >
                <a
                  class="clickable"
                  :href="getPublisherEditHref(publisher.id)"
                  @click="showForm(publisher, $event)"
                >
                  {{ publisher.name }}
                </a>
              </td>

              <!-- ID -->
              <td
                v-if="userVisibleColumns.includes('id')"
                :align="columnSettings['id'].rowAlign"
              >
                <a
                  class="clickable"
                  :href="getPublisherEditHref(publisher.id)"
                  @click="showForm(publisher, $event)"
                >
                  {{ publisher.id }}
                </a>
              </td>

              <!-- create number -->
              <td
                v-if="userVisibleColumns.includes('createNumbers')"
                v-tooltip.right.hover="$t('PUBLISHERS_CREATE_NUMBERS_TT')"
                :align="columnSettings['createNumbers'].rowAlign"
              >
                {{ publisher.createNumbers ? 'Yes' : 'No' }}
              </td>

              <!-- live -->
              <td
                v-if="userVisibleColumns.includes('currentLive')"
                :align="columnSettings['currentLive'].rowAlign"
              >
                {{ publisher.currentLive }}
              </td>

              <!-- Hour -->
              <td
                v-if="userVisibleColumns.includes('currentHour')"
                :align="columnSettings['currentHour'].rowAlign"
              >
                {{ publisher.currentHour }}
              </td>

              <!-- Day -->
              <td
                v-if="userVisibleColumns.includes('currentDay')"
                :align="columnSettings['currentDay'].rowAlign"
              >
                {{ publisher.currentDay }}
              </td>

              <!-- Month -->
              <td
                v-if="userVisibleColumns.includes('currentMonth')"
                :align="columnSettings['currentMonth'].rowAlign"
              >
                {{ publisher.currentMonth }}
              </td>

              <!-- Total -->
              <td
                v-if="userVisibleColumns.includes('total')"
                :align="columnSettings['total'].rowAlign"
              >
                {{ publisher.total }}
              </td>

              <!-- Status -->
              <td
                v-if="userVisibleColumns.includes('enabled')"
                :align="columnSettings['enabled'].rowAlign"
              >
                <Status :entity="publisher" />
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <PublisherActions
                  :create-access="hasCreateAccess"
                  :current-publisher="publisher"
                  :disabled="formActive || disabled"
                  :is-admin="isAdmin"
                  :href="getPublisherEditHref(publisher.id)"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @show-edit-form="showForm"
                  @confirm-delete="openConfirmDeletePublisherModal"
                  @show-invites="showInvites"
                  @confirm-pause="openConfirmPausePublisherModal"
                  @set-status="setPublisherStatus"
                  @show-publisher-references="showPublisherReferences"
                />
              </td>
            </tr>

            <!-- References -->
            <tr
              v-if="activeReferencesRowId === publisher.id"
              :key="publisher.id + '-references'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <div class="p-l-15 p-r-15">
                  <legend class="f-s-13 p-b-10 p-l-0">
                    <span v-t="'REFERENCES'" />
                    <button
                      type="button"
                      class="btn btn-xs btn-danger legend-right-button"
                      @click="hidePublisherReferences"
                    >
                      <i
                        aria-hidden="true"
                        class="fa fa-close"
                      />
                      Close
                    </button>
                  </legend>
                  <PublisherReferences
                    :pub-id="publisher.id"
                  />
                </div>
              </td>
            </tr>
            <!-- References -->

            <!-- Edit publisher invites row - START -->
            <tr
              v-if="publisher.showInvites"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0
              }"
            >
              <td colspan="100">
                <PublisherInvites
                  :publisher-id="publisher.id"
                  @close="hideInvites(publisher)"
                />
              </td>
            </tr>
            <!-- Edit publisher invites row - END -->

            <!-- Edit form -->
            <tr
              v-if="publisher.showEditForm"
              :key="publisher.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <PublisherForm
                  :show-loader="isUpdatePublisherLoading"
                  :publisher="publisher"
                  :account-settings="accountSettings"
                  @cancel="hideForm"
                  @submit="handleUpdatePublisherSubmit"
                />
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == publisher.id"
              :key="publisher.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <PublisherActions
                  :create-access="hasCreateAccess"
                  :current-publisher="publisher"
                  :disabled="formActive || disabled"
                  :is-admin="isAdmin"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @show-edit-form="showForm"
                  @confirm-delete="openConfirmDeletePublisherModal"
                  @show-invites="showInvites"
                  @confirm-pause="openConfirmPausePublisherModal"
                  @set-status="setPublisherStatus"
                  @show-publisher-references="showPublisherReferences"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>

      <!-- Pagination Starts -->
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
      <!-- Pagination Ends -->
    </div>

    <div
      v-if="isFetchingPublishers"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'PUBLISHERS_HEADER_LOADING'" />
    </div>

    <!-- Delete Confirmation Modal Start -->
    <PublisherDeleteModal
      v-if="showDeleteModal"
      :publisher="publisherForDelete"
      :loading-deleting-publisher="isPublisherDeleteLoading"
      :loading-publisher-references="isPublisherReferencesLoading"
      :publisher-references="publisherReferences"
      :check-references-text="checkReferencesText"
      @confirm="confirmDelete"
      @close="closeDeleteModal"
    />
    <!-- Delete Confirmation Modal End -->

    <!-- Pause Confirmation Modal Start -->
    <PublisherPauseModal
      v-if="showPauseModal"
      :publisher="publisherForPause"
      @confirm="setPublisherStatus"
      @close="closePauseModal"
    />
    <!-- Pause Confirmation Modal End -->
  </PortalWidget>
</template>

<script>
import {
  publisherService,
  utilities
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import cloneDeep from 'lodash/cloneDeep';
import { isMobile } from '@/core/utilities';
import { STATUS_ERROR, STATUS_PENDING, STATUS_SUCCESS } from '@/store/helpers';
import sharedMessages from '@/core/locales';
import messages from '../locales';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import * as actionTypes from '../store/actionTypes';
import { SET_PUBLISHER } from '../store/mutationTypes';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle';
import Loader from '@/core/components/Loader';
import PortalWidget from '@/core/components/PortalWidget.vue';
import Status from '@/core/components/Status/Status.vue';
import PublisherForm from './PublisherForm.vue';
import PublisherInvites from './PublisherInvites.vue';
import PublisherActions from './PublisherActions.vue';
import PublisherDeleteModal from './PublisherDeleteModal.vue';
import PublisherPauseModal from './PublisherPauseModal.vue';
import { csvExportService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import PublisherReferences from './PublisherReferences';

export default {
  name: 'Publishers',
  components: {
    Loader,
    PortalWidget,
    ColumnToggle,
    Status,
    PublisherForm,
    PublisherInvites,
    PublisherActions,
    PublisherDeleteModal,
    PublisherPauseModal,
    PublisherReferences
  },
  data: () => ({
    isMobile: isMobile,
    showDeleteModal: false,
    showPauseModal: false,
    showSpecificRecordDetails: false,
    activePublisher: null,
    formActive: false,
    showAddNewForm: false,
    activeActionPublisherId: false,
    isNewUserToNewPublisherInvited: false,
    currentPublisher: {},
    filters: {
      name: { value: '', keys: ['name', 'id', 'mapToTagType', 'mapToTagName'] }
    },
    currentPage: 1,
    totalPages: 0,
    activeActionRowId: null,
    activeReferencesRowId: null
  }),
  computed: {
    ...mapState(['currentAccountId']),
    ...mapState('publisher', [
      'publishers',
      'publisherReferences',
      'checkReferencesText',
      'userVisibleColumns',
      'widget',
      'invitesForPublisher',
      'showNewPublisherInvites',
      'accountSettings'
    ]),
    ...mapState('ui', ['userSettings']),
    ...mapGetters({
      isAdmin: 'isAdmin',
      hasAccessToItem: 'hasAccessToItem'
    }),
    ...mapGetters('publisher', [
      'getAccountSettingsStatus',
      'getPublishersStatus',
      'getPublisherStatus',
      'isFetchingPublishers',
      'getPublisherReferencesStatus',
      'isPublisherReferencesLoading',
      'isPublisherDeleteLoading',
      'createPublisherStatus',
      'updatePublisherStatus',
      'deletePublisherStatus'
    ]),
    ...mapGetters('ui', ['getUserSettingsStatus']),
    columnSettings() {
      return publisherService.columnSettings;
    },
    isCreatePublisherLoading() {
      return this.createPublisherStatus.status === STATUS_PENDING;
    },
    isUpdatePublisherLoading() {
      return this.updatePublisherStatus.status === STATUS_PENDING;
    },
    hasCreateAccess() {
      return this.hasAccessToItem('Affiliate', 'CREATE');
    },
    disabled() {
      return (
        !!this.activeReferencesRowId ||
        this.isPublisherDeleteLoading ||
        this.showAddNewForm ||
        !!this.currentPublisher.id
      );
    }
  },
  created() {
    this.setWidgetTitle({ title: this.$i18n.t('PUBLISHERS_HEADER') });
    this.showSpecificRecordDetails = utilities.getPropFromUrlParams(
      'affiliateId'
    );
    this.getUserSettings();
    this.loadData();
  },
  methods: {
    ...mapActions('publisher', Object.values(actionTypes)),
    ...mapActions('ui', ['getUserSettings']),
    ...mapMutations('publisher', [SET_PUBLISHER]),
    updateUserVisibleColumns() {
      this.getUserVisibleColumns();
    },
    showInvites(publisher) {
      if (!this.formActive) {
        const _publisher = cloneDeep(publisher);
        _publisher.showInvites = true;
        if (!this.showSpecificRecordDetails) this.formActive = true;
        this.activePublisher = _publisher;
        this.activeActionPublisherId = false;
        this.updatePublisherState(_publisher);
      }
    },
    hideInvites(publisher) {
      const _publisher = cloneDeep(publisher);
      _publisher.showInvites = false;

      this.updatePublisherState(_publisher);

      this.activePublisher = null;
      this.formActive = false;
    },
    showForm(publisher, $event) {
      $event?.preventDefault();

      //If references active, toggle off
      if (this.activeReferencesRowId) {
        this.hidePublisherReferences();
      }

      // return if specail `You` publisher
      if (publisher && publisher.isSelf) {
        return;
      }
      // if the invites are already opened and you click on the other publisher
      // => close the activePublisher's invite
      if (this.activePublisher && this.activePublisher.showInvites) {
        this.hideInvites(this.activePublisher);
      }

      if (!this.showSpecificRecordDetails) {
        if (this.activePublisher) {
          this.hideForm(this.activePublisher);
        }
        this.activePublisher = publisher;
        this.formActive = true;
      }
      if (publisher) {
        const _publisher = cloneDeep(publisher);
        this.showAddNewForm = false;
        _publisher.showEditForm = true;
        this.updatePublisherState(_publisher);
      } else {
        this.showAddNewForm = true;
      }
      this.activeActionPublisherId = false;
    },
    async handleUpdatePublisherSubmit(payload) {
      await this.updatePublisher(payload);

      if (this.updatePublisherStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$t('PUBLISHER_EDIT_SUCCESS')
        );
        this.onUpdate(payload.publisher);
      }

      if (this.updatePublisherStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.updatePublisherStatus.error);
      }
    },
    async handleAddPublisherSubmit(payload) {
      await this.createPublisher(payload);

      if (this.createPublisherStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$t('PUBLISHER_NOTICE_CREATE')
        );
      }
      if (this.createPublisherStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.createPublisherStatus.error);
      }
    },
    async onNewPublisherInvitesClose() {
      await this.setShowNewPublisherInvites({ showNewPublisherInvites: false });
      await this.setInvitesForPublisher({ publisher: undefined });
      this.isNewUserToNewPublisherInvited = false;
      this.hideForm();

      await this.getPublishers();
    },
    onAddingNewInviteToNewPublisher() {
      this.isNewUserToNewPublisherInvited = true;
    },
    onUpdate(publisher) {
      // Don't hide form when specific record details are only being shown
      if (!this.showSpecificRecordDetails) {
        this.hideForm(publisher);
      }
    },
    openConfirmDeletePublisherModal(publisher) {
      this.publisherForDelete = publisher;
      this.getPublisherReferences({ pubId: publisher.id });
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.publisherForDelete = null;
    },
    async confirmDelete(keepNumbers) {
      await this.deletePublisher({
        pubId: this.publisherForDelete.id,
        keepNumbers
      });

      if (this.deletePublisherStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$t('DELETE_PUBLISHER_SUCCESSFUL_MESSAGE')
        );
        this.closeDeleteModal();
      }

      if (this.deletePublisherStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.deletePublisherStatus.error);
      }
    },
    openConfirmPausePublisherModal(publisher) {
      this.publisherForPause = publisher;
      this.showPauseModal = true;
    },
    async setPublisherStatus(publisher, status) {
      if (!this.formActive) {
        publisher = publisher || this.publisherForPause;
        const _publisher = cloneDeep(publisher);
        _publisher.enabled = status;
        const payload = { publisher: _publisher };
        this.handleUpdatePublisherSubmit(payload);
        this.closePauseModal();
      }
    },
    closePauseModal() {
      this.showPauseModal = false;
      this.publisherForPause = null;
    },
    updatePublisherState(publisher) {
      if (!publisher) return;
      const publisherIndex = this.publishers.findIndex(
        (e) => e.id === publisher.id
      );
      this[SET_PUBLISHER]({ index: publisherIndex, publisher });
    },
    hideForm(publisher) {
      const _publisher = cloneDeep(publisher);
      if (_publisher) {
        _publisher.showEditForm = false;
      } else {
        this.showAddNewForm = false;
      }
      this.formActive = false;
      this.activepublisher = null;

      this.updatePublisherState(_publisher);

      if (this.showSpecificRecordDetails) {
        this.goToPublishers();
      }
    },
    goToPublishers() {
      $ngStateService.go('dashboard.affiliates');
    },
    loadData() {
      if (this.showSpecificRecordDetails) {
        this.loadPublisher(utilities.stateParams.affiliateId);
      } else {
        this.loadPublishers();
      }
      this.loadAccountSettings();
    },
    async loadAccountSettings() {
      await this.getAccountSettings();

      if (this.getAccountSettingsStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.getAccountSettingsStatus.error);
      }
    },
    async loadPublishers() {
      await this.getPublishers();

      if (this.getPublishersStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.getPublishersStatus.error);
      }
    },
    async loadPublisher(pubId) {
      await this.getPublisher({ pubId });

      if (this.getPublisherStatus.status === STATUS_SUCCESS) {
        this.showForm(this.publishers[0]);
      }

      if (this.getPublisherStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.getPublisherStatus.error);
      }
    },
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    getPublisherEditHref(publisherId) {
      return $ngStateService.href(
        'dashboard.affiliate.edit',
        { affiliateId: publisherId },
        { absolute: true }
      );
    },
    exportToCSV() {
      const exportHeaders = Object.values(this.columnSettings).map(col => ({
        label: this.$t(col.displayName),
        value: col.name
      }));
      csvExportService.exportCSVFile(exportHeaders, this.publishers, 'publishers');
    },

    async showPublisherReferences(currentPublisher) {
      //If already active, toggle off
      if (currentPublisher.id === this.activeReferencesRowId) {
        this.hidePublisherReferences();
        return;
      }

      this.activeReferencesRowId = currentPublisher.id;
    },
    hidePublisherReferences() {
      this.activeReferencesRowId = null;
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
